import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Target, AlertTriangle, Upload as UploadIcon, X, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/api/client";

/**
 * Sticky banner shown on top of the Efficiency report. Two states:
 *  - Active scope → green banner with N employees + "Ver scope" + "Limpiar
 *    scope" (admin only for clear).
 *  - No scope    → amber banner inviting the user to upload a Time Clock
 *    file. CTA navigates to /upload.
 *
 * `scope` is the response.distribution_scope object straight from
 * /api/reports/efficiency, so the parent component only has to forward it.
 */
export default function DistributionScopeBanner({ scope, onChange }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage =
    user?.role === "admin" ||
    (user?.permissions || []).includes("*") ||
    (user?.permissions || []).includes("employees:manage");

  const [showList, setShowList] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const active = !!scope?.active;
  const total = scope?.total_count || 0;
  const updated = scope?.last_updated_at
    ? new Date(scope.last_updated_at).toLocaleString(
        i18n.language === "en" ? "en-US" : "es-MX",
      )
    : null;
  const sourceTypeLabel =
    scope?.source_file_type === "TE350"
      ? t("scope.sourceTe350")
      : scope?.source_file_type === "TIME_CLOCK_MONTH"
        ? t("scope.sourceMonth")
        : scope?.source_file_type === "TIME_CLOCK_YEARLY"
          ? t("scope.sourceYearly")
          : null;

  const openList = async () => {
    setShowList(true);
    if (entries.length) return; // already loaded for this session
    setListLoading(true);
    try {
      const { data } = await api.get("/distribution-scope");
      setEntries(data.entries || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setListLoading(false);
    }
  };

  const clearScope = async () => {
    setClearing(true);
    try {
      await api.delete("/distribution-scope");
      toast.success(t("scope.cleared"));
      setConfirmClear(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClearing(false);
    }
  };

  if (!active) {
    return (
      <Alert
        className="sticky top-2 z-20 border-amber-500/40 bg-amber-50 dark:bg-amber-950/30"
        data-testid="scope-banner-empty"
      >
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700 dark:text-amber-300">
          {t("scope.bannerEmptyTitle")}
        </AlertTitle>
        <AlertDescription className="flex flex-col gap-3 text-amber-700/80 dark:text-amber-200/80 sm:flex-row sm:items-center sm:justify-between">
          <span>{t("scope.bannerEmptyBody")}</span>
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500/60 hover:bg-amber-100/60 dark:hover:bg-amber-900/40"
              onClick={() => navigate("/upload")}
              data-testid="scope-banner-upload-cta"
            >
              <UploadIcon className="mr-2 h-3.5 w-3.5" />
              {t("scope.uploadCta")}
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <Alert
        className="sticky top-2 z-20 border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30"
        data-testid="scope-banner-active"
      >
        <Target className="h-4 w-4 text-emerald-600" />
        <AlertTitle className="text-emerald-700 dark:text-emerald-300">
          {t("scope.bannerActiveTitle", { n: total })}
        </AlertTitle>
        <AlertDescription className="flex flex-col gap-3 text-emerald-700/80 dark:text-emerald-200/80 sm:flex-row sm:items-center sm:justify-between">
          <span data-testid="scope-banner-meta">
            {scope?.source_filename
              ? t("scope.bannerActiveBodyWithFile", {
                  file: scope.source_filename,
                  when: updated,
                })
              : t("scope.bannerActiveBody", { when: updated })}
            {sourceTypeLabel && (
              <Badge
                variant="outline"
                className="ml-2 border-emerald-500/40 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
                data-testid="scope-source-type"
              >
                {sourceTypeLabel}
              </Badge>
            )}
          </span>
          <div className="flex items-center gap-2">
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                className="border-rose-500/40 text-rose-700 hover:bg-rose-100/60 dark:text-rose-300 dark:hover:bg-rose-900/40"
                onClick={() => setConfirmClear(true)}
                data-testid="scope-banner-clear-btn"
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                {t("scope.clearScope")}
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>

      {/* View scope modal */}
      <Dialog open={showList} onOpenChange={setShowList}>
        <DialogContent
          className="max-h-[80vh] max-w-2xl overflow-hidden"
          data-testid="scope-list-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-emerald-500" />
              {t("scope.listTitle", { n: total })}
            </DialogTitle>
            <DialogDescription>
              {scope?.source_filename
                ? t("scope.listSubtitleWithFile", {
                    file: scope.source_filename,
                  })
                : t("scope.listSubtitle")}
            </DialogDescription>
          </DialogHeader>
          {listLoading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("common.loading")}
            </div>
          ) : (
            <div className="max-h-[55vh] space-y-1 overflow-y-auto pr-2">
              {entries.map((e) => (
                <div
                  key={e.employee_name_norm}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-card px-3 py-2 text-sm"
                  data-testid={`scope-entry-${e.employee_name_norm}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{e.employee_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {e.employee_id_raw && (
                        <span className="font-mono">#{e.employee_id_raw}</span>
                      )}
                      {e.canonical_name && (
                        <span className="ml-2">
                          → <Link to="/admin/employees-canonical" className="underline">
                            {e.canonical_name}
                          </Link>
                        </span>
                      )}
                    </div>
                  </div>
                  {e.has_canonical ? (
                    <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300">
                      {t("scope.matched")}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-300">
                      {t("scope.noCanonical")}
                    </Badge>
                  )}
                </div>
              ))}
              {!entries.length && (
                <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                  {t("common.no_data")}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="border-t pt-3">
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/distribution-scope")}
                data-testid="scope-list-manage-btn"
              >
                {t("scope.manage")}
              </Button>
            )}
            <Button size="sm" onClick={() => setShowList(false)}>
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear confirmation */}
      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent className="max-w-md" data-testid="scope-clear-dialog">
          <DialogHeader>
            <DialogTitle>{t("scope.clearTitle")}</DialogTitle>
            <DialogDescription>{t("scope.clearBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmClear(false)}
              disabled={clearing}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={clearScope}
              disabled={clearing}
              data-testid="scope-clear-confirm-btn"
            >
              {clearing ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <X className="mr-2 h-3.5 w-3.5" />
              )}
              {t("scope.clearScope")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
